<template>
  <div
    v-editable="blok"
    class="storyblok-carousel"
    :class="`storyblok-carousel--${resolvedDisplayMode}`"
  >
    <h2 v-if="blok.title" ref="carouselTitleRef" class="storyblok-carousel-title">
      {{ blok.title }}
    </h2>

    <div class="storyblok-carousel-wrapper">
      <template v-if="isSliderMode && hasMultipleSlides">
        <Button
          class="storyblok-carousel-nav storyblok-carousel-nav--overlay storyblok-carousel-nav--overlay-left"
          shape="circle"
          type="standard"
          :disabled="activeSlideIndex <= 0"
          @click="goToPreviousSlide"
          icon="chevron-back"
        />
        <Button
          class="storyblok-carousel-nav storyblok-carousel-nav--overlay storyblok-carousel-nav--overlay-right"
          shape="circle"
          type="standard"
          :disabled="activeSlideIndex >= slideBloks.length - 1"
          @click="goToNextSlide"
          icon="chevron-forward"
        />
        <div class="storyblok-carousel-dots">
          <button
            v-for="(_, index) in slideBloks"
            :key="`dot-${index}`"
            type="button"
            class="storyblok-carousel-dot"
            :class="{ 'storyblok-carousel-dot--active': index === activeSlideIndex }"
            @click="goToSlide(index)"
          />
        </div>
      </template>

      <Button
        v-if="hasMultipleSlides && !isSliderMode"
        class="storyblok-carousel-nav storyblok-carousel-nav--left"
        shape="circle"
        type="standard"
        :disabled="activeSlideIndex <= 0"
        @click="goToPreviousSlide"
        icon="chevron-back"
      />

      <div class="storyblok-carousel-viewport" ref="viewportRef" @scroll="updateActiveSlide">
        <div class="storyblok-carousel-track">
          <div
            v-for="(slideBlok, index) in slideBloks"
            :key="slideBlok._uid"
            :ref="(element) => registerSlideRef(element, index)"
            class="storyblok-carousel-slide"
            :class="{ 'single-slide': !hasMultipleSlides }"
          >
            <StoryblokComponent :blok="slideBlok" :display-mode="resolvedDisplayMode" />
          </div>
        </div>
      </div>

      <Button
        v-if="hasMultipleSlides && !isSliderMode"
        class="storyblok-carousel-nav storyblok-carousel-nav--right"
        shape="circle"
        type="standard"
        :disabled="activeSlideIndex >= slideBloks.length - 1"
        @click="goToNextSlide"
        icon="chevron-forward"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { StoryblokComponent } from '@storyblok/vue';
import { gsap } from 'gsap';
import { computed, nextTick, onBeforeUnmount, ref, watch, type ComponentPublicInstance } from 'vue';
import Button from '@/common/components/ui/button/Button.vue';
import type { StoryblokCarouselBlok } from '@/features/storyblok/types';

const props = defineProps<{
  blok: StoryblokCarouselBlok;
}>();

const carouselTitleRef = ref<HTMLElement | null>(null);
const viewportRef = ref<HTMLElement | null>(null);
const slideRefs = ref<HTMLElement[]>([]);
const activeSlideIndex = ref(0);

const slideBloks = computed(
  () => props.blok.carouselItems ?? props.blok.cards ?? props.blok.slides ?? props.blok.items ?? []
);
const resolvedDisplayMode = computed(() => props.blok.displayMode ?? 'card');
const isSliderMode = computed(() => resolvedDisplayMode.value === 'slider');
const hasMultipleSlides = computed(() => slideBloks.value.length > 1);

const registerSlideRef = (
  element: Element | ComponentPublicInstance | null,
  index: number
): void => {
  const htmlElement =
    element instanceof HTMLElement
      ? element
      : element && '$el' in element && element.$el instanceof HTMLElement
        ? element.$el
        : null;

  if (!htmlElement) {
    return;
  }

  slideRefs.value[index] = htmlElement;
};

const scrollToSlide = (index: number): void => {
  const viewport = viewportRef.value;
  const slide = slideRefs.value[index];
  if (!viewport || !slide) {
    return;
  }

  viewport.scrollTo({
    left: slide.offsetLeft,
    behavior: 'smooth',
  });
};

const goToSlide = (index: number): void => {
  const clampedIndex = Math.max(0, Math.min(index, slideBloks.value.length - 1));
  activeSlideIndex.value = clampedIndex;
  scrollToSlide(clampedIndex);
};

const goToPreviousSlide = (): void => {
  goToSlide(activeSlideIndex.value - 1);
};

const goToNextSlide = (): void => {
  goToSlide(activeSlideIndex.value + 1);
};

const updateActiveSlide = (): void => {
  const viewport = viewportRef.value;
  if (!viewport || slideRefs.value.length <= 1) {
    return;
  }

  const currentLeft = viewport.scrollLeft;
  let nearestSlideIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  slideRefs.value.forEach((slide, index) => {
    const distance = Math.abs(slide.offsetLeft - currentLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestSlideIndex = index;
    }
  });

  if (activeSlideIndex.value !== nearestSlideIndex) {
    activeSlideIndex.value = nearestSlideIndex;
  }
};

const animateSlideEntry = (index: number): void => {
  const slide = slideRefs.value[index];
  if (!slide) {
    return;
  }

  const cardElement = slide.querySelector<HTMLElement>(
    '.storyblok-image-background-card, .card, .em-card'
  );
  const titleElement = slide.querySelector<HTMLElement>(
    '.storyblok-image-background-card__title, h2, h3'
  );
  const animationTarget = cardElement ?? slide;
  const tweenTargets = [animationTarget, titleElement, carouselTitleRef.value].filter(
    (value): value is HTMLElement => !!value
  );

  gsap.killTweensOf(tweenTargets);

  const timeline = gsap.timeline();
  timeline.fromTo(
    animationTarget,
    { autoAlpha: 0.45 },
    { autoAlpha: 1, duration: 0.35, ease: 'power2.out' }
  );

  if (titleElement) {
    timeline.fromTo(
      titleElement,
      { autoAlpha: 0, y: 16 },
      { autoAlpha: 1, y: 0, duration: 0.45, ease: 'power2.out' },
      0.08
    );
  }
};

watch(
  slideBloks,
  async () => {
    slideRefs.value = [];
    activeSlideIndex.value = 0;
    await nextTick();
    goToSlide(0);
    animateSlideEntry(0);
  },
  { immediate: true }
);

watch(activeSlideIndex, (newIndex, previousIndex) => {
  if (newIndex === previousIndex) {
    return;
  }

  animateSlideEntry(newIndex);
});

watch(
  resolvedDisplayMode,
  async () => {
    await nextTick();
    goToSlide(0);
    animateSlideEntry(0);
  },
  { flush: 'post' }
);

onBeforeUnmount(() => {
  const animationTargets = slideRefs.value
    .map((slide) =>
      slide.querySelector<HTMLElement>('.storyblok-image-background-card, .card, .em-card')
    )
    .filter((value): value is HTMLElement => !!value);

  if (carouselTitleRef.value) {
    animationTargets.push(carouselTitleRef.value);
  }

  gsap.killTweensOf(animationTargets);
});
</script>

<style scoped>
.storyblok-carousel {
  margin: 0;
  padding-bottom: var(--ion-space-6);
}

.storyblok-carousel-title {
  margin: 0 0 var(--ion-space-3);
  color: var(--ion-text-color-primary);
}

.storyblok-carousel-wrapper {
  display: flex;
  align-items: center;
  gap: var(--ion-space-3);
  position: relative;
}

.storyblok-carousel-viewport {
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  flex: 1;
}

.storyblok-carousel-viewport::-webkit-scrollbar {
  display: none;
}

.storyblok-carousel-track {
  display: flex;
  gap: var(--ion-space-3);
  align-items: stretch;
}

.storyblok-carousel-slide {
  --storyblok-card-per-view: 3;
  flex: 0 0
    calc(
      (100% - ((var(--storyblok-card-per-view) - 1) * var(--ion-space-3))) /
        var(--storyblok-card-per-view)
    );
  scroll-snap-align: start;
  display: flex;
}

.storyblok-carousel-slide > * {
  width: 100%;
}

.storyblok-carousel-slide.single-slide {
  flex-basis: 100%;
}

.storyblok-carousel-nav {
  min-width: 44px;
}

.storyblok-carousel--slider .storyblok-carousel-wrapper {
  display: block;
}

.storyblok-carousel--slider .storyblok-carousel-viewport {
  position: relative;
  overflow: hidden;
}

.storyblok-carousel--slider .storyblok-carousel-track {
  gap: 0;
}

.storyblok-carousel--slider .storyblok-carousel-slide {
  flex-basis: 100%;
}

.storyblok-carousel-nav--overlay {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
}

.storyblok-carousel-nav--overlay-left {
  left: var(--ion-space-4);
}

.storyblok-carousel-nav--overlay-right {
  right: var(--ion-space-4);
}

.storyblok-carousel-dots {
  position: absolute;
  left: 50%;
  bottom: var(--ion-space-4);
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  gap: var(--ion-space-2);
}

.storyblok-carousel-dot {
  width: 8px;
  height: 8px;
  border: 0;
  border-radius: 50%;
  padding: 0;
  background: rgba(255, 255, 255, 0.5);
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
}

.storyblok-carousel-dot--active {
  background: rgba(255, 255, 255, 0.95);
  transform: scale(1.15);
}

@media (max-width: 1024px) {
  .storyblok-carousel-slide {
    --storyblok-card-per-view: 2;
  }
}

@media (max-width: 768px) {
  .storyblok-carousel-slide {
    --storyblok-card-per-view: 1;
  }

  .storyblok-carousel-nav--overlay-left {
    left: var(--ion-space-2);
  }

  .storyblok-carousel-nav--overlay-right {
    right: var(--ion-space-2);
  }
}

@media (min-width: 1600px) {
  .storyblok-carousel-slide {
    --storyblok-card-per-view: 4;
  }
}
</style>
